<template>
  <div class="form-page-container">
    <PremiumPage
      title="Edit Notice"
      subtitle="Update existing notice details and attachments"
      showBack
      show-footer-buttons
      submit-label="Submit"
      :loading="loading"
      @cancel="$router.go(-1)"
      @submit="saveNoticeDetail"
    >
      <div class="pa-4">
        <v-form>
          <div class="half-width-wrap">
            <v-row>
              <v-col cols="12">
                <PremiumInput
                  v-model="notice.title"
                  label="Title *"
                  placeholder="Enter a descriptive title"
                  icon="mdi-format-title"
                  v-validate="'required'"
                  name="title"
                  :error="errors.first('title') || serverSideErrors.title"
                />
              </v-col>
            </v-row>

            <v-row class="mt-4">
              <v-col cols="12">
                <PremiumInput
                  v-model="notice.description"
                  label="Description *"
                  placeholder="Details of the notice..."
                  multiline
                  rows="4"
                  resizable
                  v-validate="'required'"
                  name="description"
                  :error="errors.first('description') || serverSideErrors.description"
                />
              </v-col>
            </v-row>

            <v-row class="mt-4">
              <v-col cols="12">
                <PremiumDatePicker
                  v-model="notice.expiry_date"
                  label="Expiry Date"
                  placeholder="Select expiry date"
                  name="ExpiryDate"
                  :error="
                    errors.first('ExpiryDate') || serverSideErrors.expiry_date
                  "
                />
              </v-col>
            </v-row>
          </div>

          <v-row class="mt-4">
            <v-col cols="6">
              <div class="premium-field-label mb-3">Attachments</div>
              <input
                type="file"
                ref="attachInput"
                multiple
                accept="image/*,.pdf"
                hidden
                @change="onAttachChange"
              />
              <!-- Drop Zone -->
              <div
                class="attach-dropzone"
                :class="{ 'attach-dropzone-active': dragging }"
                @click="$refs.attachInput.click()"
                @dragover.prevent="dragging = true"
                @dragleave="dragging = false"
                @drop.prevent="onDrop"
              >
                <v-icon size="28" color="var(--tx3)">mdi-paperclip</v-icon>
                <div class="dropzone-label">Click or drag files here</div>
                <div class="dropzone-hint">JPG, PNG, PDF allowed</div>
              </div>

              <!-- Preview Grid -->
              <div
                v-if="attachPreviews.length"
                class="attach-preview-grid mt-3"
              >
                <div
                  v-for="(p, i) in attachPreviews"
                  :key="i"
                  class="attach-preview-item"
                  @click="openMediaViewer(added_documents.length + i)"
                >
                  <img
                    v-if="p.isImage"
                    :src="p.url"
                    class="attach-preview-img"
                  />
                  <div v-else class="attach-preview-pdf">
                    <v-icon size="32" color="#EF4444">mdi-file-pdf-box</v-icon>
                    <span class="attach-preview-name">{{ p.name }}</span>
                  </div>
                  <button
                    class="attach-remove-btn"
                    @click.stop="removeAttach(i)"
                  >
                    <v-icon size="14" color="white">mdi-close</v-icon>
                  </button>
                </div>
              </div>
            </v-col>
          </v-row>

          <v-row class="mt-8" v-if="added_documents.length">
            <v-col cols="12">
              <div class="premium-field-label mb-4">Existing Attachments</div>
              <div class="existing-attach-grid">
                <div
                  v-for="(item, index) in added_documents"
                  :key="item.document_url"
                >
                  <div class="attachment-card-premium">
                    <v-img
                      v-if="item.file_type == 'image'"
                      :src="base_url + '/uploads/' + item.document_url"
                      aspect-ratio="1"
                      class="attachment-img"
                      contain
                    />
                    <div v-else class="attachment-placeholder">
                      <v-icon color="#EF4444" size="40"
                        >mdi-file-pdf-box</v-icon
                      >
                    </div>

                    <div class="attachment-overlay">
                      <v-btn icon color="white" @click="openMediaViewer(index)">
                        <v-icon>mdi-eye-outline</v-icon>
                      </v-btn>
                      <v-btn icon color="error" @click="confirmDelete(item.id)">
                        <v-icon>mdi-trash-can-outline</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-form>
      </div>
    </PremiumPage>

    <!-- Media Viewer -->
    <MediaViewer
      v-model="mediaDialog"
      :items="mediaItems"
      :index="mediaIndex"
    />

    <!-- Confirm Delete Dialog -->
    <v-dialog v-model="deleteDialog" width="400">
      <v-card class="rounded-xl pa-4">
        <v-card-title
          class="headline flex-column align-center text-center pb-2"
        >
          <v-icon color="error" size="48" class="mb-2"
            >mdi-alert-circle-outline</v-icon
          >
          <div>Confirm Deletion</div>
        </v-card-title>
        <v-card-text class="text-center text-subtitle-1">
          Are you sure you want to delete this attachment?
        </v-card-text>
        <v-card-actions class="justify-center gap-2">
          <v-btn text @click="closeDialog" class="rounded-lg">Cancel</v-btn>
          <v-btn
            depressed
            color="error"
            @click="deleteDocument(delete_id)"
            class="rounded-lg px-6"
            >Yes, Delete</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Media Viewer -->
    <MediaViewer
      v-model="mediaDialog"
      :items="mediaItems"
      :index="mediaIndex"
    />
  </div>
</template>

<script>
import { mapMutations } from "vuex";
import moment from "moment";
import PremiumPage from "../../common/PremiumPage.vue";
import PremiumInput from "../../common/PremiumInput.vue";
import PremiumDatePicker from "../../common/PremiumDatePicker.vue";
import MediaViewer from "../../common/MediaViewer.vue";

export default {
  name: "EditNotice",
  components: { PremiumPage, PremiumInput, PremiumDatePicker, MediaViewer },
  data() {
    return {
      user: {},
      loading: false,
      serverSideErrors: {},
      notice: {
        title: "",
        description: "",
        documents: [],
        expiry_date: "",
      },
      added_documents: [],
      delete_id: "",
      deleteDialog: false,
      imageDialog: false,
      image_url: "",
      base_url: process.env.VUE_APP_API_URL,
      menu: false,
      attachPreviews: [],
      dragging: false,
      mediaDialog: false,
      mediaIndex: 0,
    };
  },
  computed: {
    today() {
      return moment().format("YYYY-MM-DD");
    },
    mediaItems() {
      const existing = this.added_documents.map((doc) => ({
        url: this.base_url + "/uploads/" + doc.document_url,
        type: doc.file_type === "image" ? "image" : "document",
        name: doc.document_url,
      }));
      const newOnes = this.attachPreviews.map((p) => ({
        url: p.url,
        type: p.isImage ? "image" : "document",
        name: p.name,
      }));
      return [...existing, ...newOnes];
    },
  },
  methods: {
    ...mapMutations("alert", ["setAlert"]),
    saveNoticeDetail() {
      let _self = this;
      this.$validator.validateAll().then((result) => {
        if (result) {
          _self.loading = true;
          let formData = new FormData();

          formData.append("title", this.notice.title);
          formData.append("description", this.notice.description);
          formData.append(
            "expiry_date",
            this.notice.expiry_date
              ? moment.utc(this.notice.expiry_date).valueOf()
              : "",
          );

          if (this.notice.documents) {
            for (let i = 0; i < this.notice.documents.length; i++) {
              formData.append("documents[]", this.notice.documents[i]);
            }
          }

          _self.$axios
            .put("/societyadmin/notices/" + this.notice.id, formData, {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            })
            .then((response) => {
              let data = response.data;

              _self.loading = false;
              _self.serverSideErrors = {};

              if (data.status) {
                this.setAlert({
                  showAlert: true,
                  message: data.message,
                  variant: "green accent-4",
                  timeout: 2000,
                });
                _self.$router.push({ path: "/societyadmin/notice" });
              } else {
                if (!data.errors) {
                  this.setAlert({
                    showAlert: true,
                    message: data.message,
                    variant: "red accent-3",
                    timeout: 2000,
                  });
                } else {
                  for (let i = 0; i < data.errors.length; i++) {
                    this.serverSideErrors[data.errors[i].field] =
                      data.errors[i].message;
                  }
                }
              }
            });
        }
      });
    },
    openMediaViewer(index) {
      this.mediaIndex = index;
      this.mediaDialog = true;
    },
    getNoticeDetail() {
      let _self = this;
      _self.$axios
        .get("societyadmin/notices/" + _self.$route.params.id)
        .then((response) => {
          let data = response.data;
          if (data.status) {
            _self.notice = data.notice;
            data.notice.expiry_date = data.notice.expiry_date
              ? moment(Number(data.notice.expiry_date)).format("YYYY-MM-DD")
              : null;

            _self.added_documents = data.notice.documents || [];
            if (!this.notice.documents) this.notice.documents = [];
          } else {
            this.setAlert({
              showAlert: true,
              variant: "red accent-3",
              timeout: "2000",
              message: data.message,
            });
          }
        });
    },
    confirmDelete(id) {
      this.deleteDialog = true;
      this.delete_id = id;
    },
    deleteDocument(Id) {
      let _self = this;
      _self.$axios
        .delete("societyadmin/notices/documents/" + Id)
        .then((response) => {
          let data = response.data;
          if (data.status) {
            this.getNoticeDetail();
            this.deleteDialog = false;
            this.setAlert({
              showAlert: true,
              variant: "green accent-4",
              timeout: "2000",
              message: data.message,
            });
          } else {
            this.setAlert({
              showAlert: true,
              variant: "red accent-3",
              timeout: "2000",
              message: data.message,
            });
          }
        });
    },
    closeDialog() {
      this.deleteDialog = false;
    },
    onAttachChange(e) {
      this.addFiles(Array.from(e.target.files));
      e.target.value = "";
    },
    onDrop(e) {
      this.dragging = false;
      this.addFiles(Array.from(e.dataTransfer.files));
    },
    addFiles(files) {
      if (!this.notice.documents) this.notice.documents = [];
      files.forEach((file) => {
        this.notice.documents.push(file);
        const isImage = file.type.startsWith("image/");
        if (isImage) {
          const url = URL.createObjectURL(file);
          this.attachPreviews.push({ url, name: file.name, isImage: true });
        } else {
          this.attachPreviews.push({
            url: null,
            name: file.name,
            isImage: false,
          });
        }
      });
    },
    removeAttach(i) {
      this.notice.documents.splice(i, 1);
      this.attachPreviews.splice(i, 1);
    },
  },
  mounted() {
    this.user = JSON.parse(localStorage.getItem("user"));
    this.getNoticeDetail();
  },
};
</script>

<style scoped>
.form-page-container {
  padding: 24px;
}

/* ── 50% width wrapper ── */
.half-width-wrap {
  max-width: 50%;
}
@media (max-width: 960px) {
  .half-width-wrap {
    max-width: 100%;
  }
}

.premium-field-label {
  font-size: 12px;
  font-weight: 700;
  color: var(--tx3, #64748b);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* ── Custom Textarea ── */
::v-deep .premium-textarea .v-input__slot {
  border-radius: 12px !important;
  background: var(--surface2, #f8fafc) !important;
  border: 1px solid var(--border, #e2e8f0) !important;
  box-shadow: none !important;
}
::v-deep .premium-textarea fieldset {
  border: none;
}

/* ── File Input ── */
::v-deep .premium-file-input .v-input__slot {
  border-radius: 12px !important;
  background: var(--surface2, #f8fafc) !important;
  border: 1px dashed var(--border, #e2e8f0) !important;
}
::v-deep .premium-file-input fieldset {
  border: none;
}

/* ── Attachments Grid ── */
.attachment-card-premium {
  height: 160px;
  width: 160px;
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  background: var(--surface2);
  border: 1px solid var(--border);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
}
.attachment-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.attachment-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fef2f2;
}

.attachment-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  opacity: 0;
  transition: opacity 0.2s;
}
.attachment-card-premium:hover .attachment-overlay {
  opacity: 1;
}

/* ── Attachment Uploader ── */
.attach-dropzone {
  border: 2px dashed var(--border, #e2e8f0);
  border-radius: 14px;
  background: var(--surface2, #f8fafc);
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
}
.attach-dropzone:hover {
  border-color: var(--brand);
  background: rgba(13, 153, 114, 0.04);
}
.attach-dropzone-active {
  border-color: var(--brand) !important;
  background: rgba(13, 153, 114, 0.06) !important;
}
.dropzone-label {
  font-size: 13px;
  font-weight: 700;
  color: var(--tx2);
  margin-top: 4px;
}
.dropzone-hint {
  font-size: 11.5px;
  color: var(--tx3);
  font-weight: 500;
}

.attach-preview-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.attach-preview-item {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid var(--border);
  flex-shrink: 0;
  background: var(--surface2);
}
.attach-preview-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.attach-preview-pdf {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 4px;
}
.attach-preview-name {
  font-size: 9px;
  color: var(--tx3);
  text-align: center;
  word-break: break-all;
  line-height: 1.2;
}
.attach-remove-btn {
  position: absolute;
  top: 3px;
  right: 3px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s;
}
.attach-remove-btn:hover {
  background: #ef4444;
}

/* ── Existing Attachments Grid ── */
.existing-attach-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.error-text {
  font-size: 12px;
  color: #ef4444;
  margin-top: 4px;
  font-weight: 500;
}

.cancel-btn {
  color: var(--tx2, #374151);
  font-weight: 600;
  border-radius: 8px;
  text-transform: none;
}
.save-btn {
  border-radius: 8px !important;
  font-size: 14px;
  font-weight: 700;
  padding: 0 32px !important;
  height: 44px !important;
  text-transform: none !important;
}
</style>
